import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  apiGetPublicAdvancingShare,
  apiValidateAdvancingShare,
  type AdvancingSharePublicDTO,
  type AdvancingShareItem,
} from '../api';
import PageHeader from '../components/PageHeader';

// Display titles for the request sections shown to the organiser. A link is
// scoped server-side, so only the section(s) for its scope are ever returned.
const SECTION_ORDER = ['iem', 'backline', 'sound', 'lights', 'hospitality', 'wireless', 'hotel', 'logistics'];
const SECTION_TITLE_KEYS: Record<string, string> = {
  iem: 'advancingShare.sectionIem',
  backline: 'advancingShare.sectionBackline',
  sound: 'advancingShare.sectionSound',
  lights: 'advancingShare.sectionLights',
  hospitality: 'advancingShare.sectionHospitality',
  wireless: 'advancingShare.sectionWireless',
  hotel: 'advancingShare.sectionHotel',
  logistics: 'advancingShare.sectionLogistics',
};
const UNKNOWN_SECTION = 'other';

// FULL RIDER VIEW (option A): display order + titles for the embedded rider's
// sections (same sections the /share/rider page groups).
const RIDER_SECTION_ORDER = ['sound', 'iem', 'backline', 'lights', 'hospitality', 'stageDesign', 'wireless'];
const RIDER_SECTION_TITLE: Record<string, string> = {
  sound: 'shareRider.sectionSound',
  iem: 'shareRider.sectionIem',
  backline: 'shareRider.sectionBackline',
  lights: 'shareRider.sectionLights',
  hospitality: 'shareRider.sectionHospitality',
  stageDesign: 'shareRider.sectionStageDesign',
  wireless: 'shareRider.sectionWireless',
};

// Friendly page titles per scope.
const SCOPE_TITLE_KEYS: Record<string, string> = {
  tech: 'advancingShare.scopeTech',
  hotel: 'advancingShare.scopeHotel',
  logistics: 'advancingShare.scopeLogistics',
  all: 'advancingShare.scopeAll',
};

// Pull the server's plain-text `error` out of an ApiError so the user sees the
// real reason (e.g. "Hotel is booked by the band — ...") instead of the JSON dump.
function serverErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const b = err.body as Record<string, unknown>;
    if (typeof b.error === 'string' && b.error) return b.error;
    if (typeof b.message === 'string' && b.message) return b.message;
  }
  return err instanceof Error ? err.message : fallback;
}

/**
 * PUBLIC page reached from an advancing share link (/share/advancing/:token) —
 * no login, no session. The token carries the link's scope, so the organiser
 * sees ONLY their category (tech / hotel / logistics / all) and can confirm or
 * decline each item with a remark. The scope filter is enforced server-side.
 */
export default function AdvancingSharePage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [data, setData] = useState<AdvancingSharePublicDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Local working copy of checks + remarks, keyed by itemKey.
  const [edits, setEdits] = useState<Record<string, { confirmed: boolean; remarks: string }>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [saveState, setSaveState] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    if (!token) { setError(t('advancingShare.missingToken')); setLoading(false); return; }
    let cancelled = false;
    apiGetPublicAdvancingShare(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(serverErrorText(e, t('advancingShare.invalidOrDisabled'))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const items = useMemo(() => (data ? data.items : []), [data]);

  const confirmedOf = useCallback((key: string): boolean => {
    if (key in edits) return edits[key].confirmed;
    const it = items.find((i) => i.itemKey === key);
    return !!it?.confirmed;
  }, [edits, items]);

  const remarksOf = useCallback((key: string): string => {
    if (key in edits) return edits[key].remarks;
    const it = items.find((i) => i.itemKey === key);
    return it?.remarks ?? '';
  }, [edits, items]);

  async function save(key: string, confirmed: boolean, remarks: string) {
    setSavingKeys((prev) => new Set(prev).add(key));
    setSaveState(null);
    try {
      await apiValidateAdvancingShare(token, key, confirmed, remarks);
      setSaveState({ ok: true, msg: t('advancingShare.saved') });
      setData((prev) => prev
        ? { ...prev, items: prev.items.map((i) => i.itemKey === key ? { ...i, confirmed: confirmed ? 1 : 0, remarks } : i) }
        : prev);
    } catch (e) {
      setSaveState({ ok: false, msg: serverErrorText(e, t('advancingShare.saveError')) });
    } finally {
      setSavingKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  const onToggle = (key: string, now: boolean) => {
    setEdits((prev) => ({ ...prev, [key]: { confirmed: now, remarks: remarksOf(key) } }));
    void save(key, now, remarksOf(key));
  };

  const onRemarks = (key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: { confirmed: confirmedOf(key), remarks: value } }));
  };

  const onRemarksBlur = (key: string) => {
    void save(key, confirmedOf(key), remarksOf(key));
  };

  // Group items by section for a clean per-category display.
  const bySection = useMemo(() => {
    const map = new Map<string, AdvancingShareItem[]>();
    for (const it of items) {
      const sec = it.section && SECTION_ORDER.includes(it.section) ? it.section : UNKNOWN_SECTION;
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(it);
    }
    const ordered: Array<{ key: string; titleKey: string; items: AdvancingShareItem[] }> = [];
    for (const sec of SECTION_ORDER) {
      if (map.has(sec)) ordered.push({ key: sec, titleKey: SECTION_TITLE_KEYS[sec] || sec, items: map.get(sec)! });
    }
    if (map.has(UNKNOWN_SECTION)) ordered.push({ key: UNKNOWN_SECTION, titleKey: 'advancingShare.sectionOther', items: map.get(UNKNOWN_SECTION)! });
    return ordered;
  }, [items]);

  const confirmedCount = items.filter((i) => !!i.confirmed).length;

  if (loading) return <div className="page-pad">{t('advancingShare.loading')}</div>;

  if (error || !data) {
    return (
      <div className="page-pad" style={{ maxWidth: 640, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('advancingShare.unavailable')}</h2>
          <p style={{ color: 'var(--text-dim)' }}>{error ?? t('advancingShare.invalidLink')}</p>
          <p>{t('advancingShare.noLogin')}</p>
        </div>
      </div>
    );
  }

  const scopeTitle = t(SCOPE_TITLE_KEYS[data.scope] || 'advancingShare.scopeAll');
  const show = data.show;
  const dateLabel = show?.date ? new Date(show.date).toLocaleDateString() : '';

  return (
    <div ref={denseRef} className="page rider-page">
      <PageHeader
        title={`${data.band.name || show?.band_name || 'Band'} — ${scopeTitle}`}
        actions={
          <>
            <span className="badge badge-muted">{scopeTitle}</span>
            {saveState && (
              <span style={{ color: saveState.ok ? 'var(--success)' : 'var(--danger)', fontSize: '0.85rem' }}>
                {saveState.msg}
              </span>
            )}
          </>
        }
      />

      {/* Show context */}
      {(show || data.band.name) && (
        <div className="card" style={{ padding: 16, marginBottom: 18 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
            {show?.name && <span><strong>{show.name}</strong></span>}
            {dateLabel && <span>{dateLabel}</span>}
            {show?.venue && <span>{show.venue}{show.city ? `, ${show.city}` : ''}</span>}
            {/* The show's shared tech rider (tech/all links only): the organiser
                answers the requests from here — the rider is one click away. */}
            {/* OPTION A (2026-10-24): the FULL rider is now EMBEDDED below (see
                rider_view card) — no separate link, the organiser never leaves
                this page. (The old /share/rider URL redirects here.) */}
          </div>
        </div>
      )}

      <p style={{ color: 'var(--text-dim)', marginBottom: 18 }}>
        {t('advancingShare.validationHint')}
      </p>

      {/* Summary bar */}
      <div className="card" style={{ padding: 12, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>
          {t('advancingShare.providedCount', { confirmed: confirmedCount, total: items.length })}
        </span>
        {confirmedCount === items.length && items.length > 0 && (
          <span style={{ color: 'var(--success, #2e7d32)', fontWeight: 600 }}>{t('advancingShare.everythingConfirmed')}</span>
        )}
      </div>

      {/* Request sections (grouped by the link's scope) */}
      {bySection.length === 0 && <p className="empty-hint">{t('advancingShare.nothingToValidate')}</p>}
      {bySection.map((group) => (
        <div className="card" style={{ padding: 20, marginBottom: 18 }} key={group.key}>
          <h2 style={{ marginTop: 0, marginBottom: 12, borderBottom: '1px solid var(--divider)', paddingBottom: 6 }}>
            {t(group.titleKey)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {group.items.map((it) => (
              <ItemRow
                key={it.itemKey}
                item={it}
                confirmed={confirmedOf(it.itemKey)}
                remarks={remarksOf(it.itemKey)}
                saving={savingKeys.has(it.itemKey)}
                onToggle={(v) => onToggle(it.itemKey, v)}
                onRemarks={(v) => onRemarks(it.itemKey, v)}
                onRemarksBlur={() => onRemarksBlur(it.itemKey)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* FULL RIDER VIEW (option A, user 2026-10-24): the show's complete rider
          in a collapsible card — the organiser never leaves this page. Items
          grouped by section with their provider (band = info, venue = request),
          + input/output lists; same data as the old /share/rider page (now
          redirected here). Collapsed by default: the validation grid above
          stays the primary surface. */}
      {data.rider_view && data.rider_view.items.length > 0 && (
        <details className="card" style={{ padding: 20, marginBottom: 18 }} data-advancing-rider-view>
          <summary style={{ fontWeight: 600, borderBottom: '1px solid var(--divider)', paddingBottom: 6 }}>
            <h3 style={{ margin: 0, display: 'inline' }}>{t('advancingShare.fullRiderTitle')}</h3>
            {' '}({data.rider_view.items.length} {t('advancingShare.fullRiderItems')})
          </summary>
          {RIDER_SECTION_ORDER.filter((s) => data.rider_view!.items.some((it) => it.section === s)).map((s) => (
            <div key={s} style={{ marginBottom: 14 }}>
              <h3 style={{ margin: '8px 0 6px', fontSize: 'var(--font-md)', borderBottom: '1px solid var(--divider)', paddingBottom: 4 }}>
                {t(RIDER_SECTION_TITLE[s] || s)}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.rider_view!.items.filter((it) => it.section === s).map((it) => (
                  <div key={it.itemKey} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 'var(--font-sm)' }}>
                    <span style={{ fontWeight: 600 }}>{it.itemLabel}</span>
                    {it.detail && <span style={{ color: 'var(--text-dim)' }}>{it.detail}</span>}
                    <span style={{ marginLeft: 'auto', flexShrink: 0, color: it.provider === 'band' ? 'var(--text-dim)' : 'var(--accent)', fontSize: 'var(--font-xs)' }}>
                      {it.provider === 'band' ? t('advancingShare.provBand') : t('advancingShare.provVenue')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {/* Input / Output lists (channel-level detail) */}
          {(data.rider_view.inputList.length > 0 || data.rider_view.outputList.length > 0) && (
            <div style={{ marginTop: 10 }}>
              <h3 style={{ margin: '8px 0 6px', fontSize: 'var(--font-md)' }}>{t('advancingShare.fullRiderIoTitle')}</h3>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {(['inputList', 'outputList'] as const).map((listKey) => (
                  (data.rider_view![listKey].length > 0) && (
                    <div className="table-wrapper" key={listKey}>
                    <table className="tbl-dense" style={{ borderCollapse: 'collapse', fontSize: 'var(--font-xs)' }}>
                      <thead>
                        <tr><th style={{ textAlign: 'left', padding: '2px 8px' }}>{t(listKey === 'inputList' ? 'advancingShare.fullRiderInputs' : 'advancingShare.fullRiderOutputs')}</th></tr>
                      </thead>
                      <tbody>
                        {data.rider_view![listKey].map((tr: any, i: number) => (
                          <tr key={i}><td style={{ padding: '1px 8px' }}>{tr.ch ? `${tr.ch} — ` : ''}{tr.name || tr.label || ''}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </details>
      )}

      <p style={{ color: 'var(--text-dim)', textAlign: 'center', fontSize: '0.85rem' }}>
        {t('advancingShare.publicPage')}<Link to="/" style={{ textDecoration: 'underline' }}>Bandz</Link>
      </p>
    </div>
  );
}

function ItemRow({ item, confirmed, remarks, saving, onToggle, onRemarks, onRemarksBlur }: {
  item: AdvancingShareItem;
  confirmed: boolean;
  remarks: string;
  saving: boolean;
  onToggle: (v: boolean) => void;
  onRemarks: (v: string) => void;
  onRemarksBlur: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px',
      border: '1px solid var(--border)', borderRadius: 8,
      background: confirmed ? 'rgba(0,212,126,.06)' : 'transparent',
    }}>
      <input
        type="checkbox"
        checked={confirmed}
        disabled={saving}
        style={{ transform: 'scale(1.3)', cursor: 'pointer', flexShrink: 0 }}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--font-md)' }}>{item.itemLabel}</span>
          {item.quantity && item.quantity > 1 && (
            <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>{t('advancingShare.qty', { count: item.quantity })}</span>
          )}
        </div>
        {item.detail && <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-dim)', marginTop: 2 }}>{item.detail}</div>}
      </div>
      <input
        className="form-input"
        value={remarks}
        placeholder={t('advancingShare.remarkPh')}
        style={{ width: 220, flexShrink: 0 }}
        onChange={(e) => onRemarks(e.target.value)}
        onBlur={onRemarksBlur}
      />
    </div>
  );
}
